"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useEffect, useTransition } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { addExpense, editExpense } from "@/actions/expense";
import { AnimatedFading } from "@/components/animations/AnimatedFading";
import { Amount } from "@/components/expense/fields/Amount";
import { CurrencySelector } from "@/components/expense/fields/CurrencySelector";
import { DatePicker } from "@/components/expense/fields/DatePicker";
import { FolderSelector } from "@/components/expense/fields/FolderSelector";
import { Imputation } from "@/components/expense/fields/Imputation";
import { JournalSelector } from "@/components/expense/fields/JournalSelector";
import { MovedAccountSelector } from "@/components/expense/fields/MovedAccountSelector";
import { Object } from "@/components/expense/fields/Object";
import { PieceNumber } from "@/components/expense/fields/PieceNumber";
import { Signature } from "@/components/expense/fields/Signature";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Form, FormField } from "@/components/ui/form";
import { Typography } from "@/components/ui/typography";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useRouter } from "@/i18n/routing";
import { ROUTES } from "@/routes";
import { ExpenseSchema, TExpenseSchema } from "@/schemas";

interface IExpenseFormProps {
    initialData?: TExpenseSchema & { id?: string };
    mode?: "create" | "edit";
}

export const ExpenseForm = ({ initialData, mode = "create" }: IExpenseFormProps) => {
    const t = useTranslations("expense");

    const user = useCurrentUser();

    const [ isPending, startTransition ] = useTransition();

    const queryClient = useQueryClient();

    const router = useRouter();

    const formSchema = ExpenseSchema(t);
    const form = useForm<TExpenseSchema>({
        resolver: zodResolver(formSchema),
        defaultValues: initialData || {
            pieceNumber: "",
            amount: 0,
            signature: "",
        },
    });

    useEffect(() => {
        if (user && !form.getValues("signature") && mode === "create") {
            form.setValue("signature", user.firstName[0] + user.lastName[0], {
                shouldDirty: false,
                shouldTouch: false,
            });
        }
    }, [ user, form ]);

    const onSubmit = (values: TExpenseSchema) => {
        const action = mode === "create" ? addExpense : editExpense;

        startTransition(() => {
            action({
                ...values,
                ...(mode === "edit" && initialData?.id && { id: initialData.id }),
            } as TExpenseSchema & { id: string }).then(data => {
                if (data.error) {
                    toast.error(data.error);
                }
                if (data.success) {
                    toast.success(data.success, {
                        action: {
                            label: t("success.viewExpense"),
                            onClick: () => {
                                const folderPath = data.folderPath || "/";
                                router.push(`${ ROUTES.INVOICE_FOLDERS }?path=${ folderPath }`, {
                                    scroll: false,
                                });
                            },
                        },
                        duration: 5000,
                    });

                    queryClient.invalidateQueries({ queryKey: [ "lastUsedMovedAccounts" ] });

                    if (mode === "create") {
                        form.setValue("pieceNumber", "");
                        form.setValue("object", "");
                        form.setValue("amount", 0);
                    }
                }
            });
        });
    };

    return (
        <AnimatedFading id="expense-form">
            <Card className="w-[360px] md:w-[700px]">
                <CardHeader>
                    <Typography variant={ "h1" } className="text-center text-4xl uppercase lg:text-4xl">
                        {t("title")}
                    </Typography>
                </CardHeader>
                <CardContent>
                    <Form { ...form }>
                        <form onSubmit={ form.handleSubmit(onSubmit) } className="space-y-4">
                            <FormField
                                control={ form.control }
                                name="journalId"
                                render={ ({ field }) => <JournalSelector value={ field.value } onChange={ field.onChange } isPending={ isPending } /> }
                            />
                            <FormField
                                control={ form.control }
                                name="date"
                                render={ ({ field }) => <DatePicker fieldValue={ field.value } onChange={ field.onChange } isPending={ isPending } /> }
                            />
                            <FormField
                                control={ form.control }
                                name="movedAccountId"
                                render={ ({ field }) => {
                                    return (
                                        <>
                                            <MovedAccountSelector value={ field.value } onChange={ field.onChange } isPending={ isPending } />
                                            <Imputation value={ field.value } isPending={ isPending } />
                                        </>
                                    );
                                } }
                            />
                            <FormField
                                control={ form.control }
                                name="pieceNumber"
                                render={ ({ field }) => <PieceNumber { ...field } isPending={ isPending } /> }
                            />
                            <FormField control={ form.control } name="object" render={ ({ field }) => <Object { ...field } isPending={ isPending } /> } />
                            <FormField control={ form.control } name="amount" render={ ({ field }) => <Amount { ...field } isPending={ isPending } /> } />
                            <FormField
                                control={ form.control }
                                name="currencyId"
                                render={ ({ field }) => <CurrencySelector value={ field.value } onChange={ field.onChange } isPending={ isPending } /> }
                            />
                            <FormField
                                control={ form.control }
                                name="signature"
                                render={ ({ field }) => <Signature { ...field } loading={ !user } isPending={ isPending } /> }
                            />
                            <FormField
                                control={ form.control }
                                name="folderId"
                                render={ ({ field }) => <FolderSelector value={ field.value } onChange={ field.onChange } isPending={ isPending } /> }
                            />
                            <Button disabled={ isPending } type="submit" className="w-full">
                                {t("submitButton")}
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </AnimatedFading>
    );
};
